'use client';

import { X } from '@phosphor-icons/react/ssr';
import { type ComponentProps, type ReactNode, useId } from 'react';

import {
  Popover as PopoverRoot,
  PopoverAnchor,
  PopoverClose,
  PopoverContent as PopoverContentPrimitive,
  PopoverTrigger,
} from '../ui/popover';
import { Separator } from '../ui/separator';
import { IconButton } from './icon-button';
import { popoverStyles } from './overlay-styles';

export type PopoverSize = 'sm' | 'md' | 'lg';

type SectionProps = {
  /** Figma "Size": Small 280 · Medium 320 (default) · Large 400. */
  size?: PopoverSize;
  /**
   * Figma "Title" - shows the Header (Figma "Header" on) with a Close button.
   * Without a title, name the panel with `aria-label`.
   */
  title?: ReactNode;
  /** Figma Close button in the Header. On by default when there is a title. */
  showClose?: boolean;
  /** Accessible name of the Close button. */
  closeLabel?: string;
  /**
   * Figma "Footer": the actions, right-aligned - a Small Ghost secondary
   * ("Reset", Figma "Secondary action") then a Small Primary ("Apply").
   */
  footer?: ReactNode;
  /** Figma "Content": the body (space/4 padding; scrolls when the panel is capped). */
  children?: ReactNode;
};

function PopoverSections({
  titleId,
  title,
  showClose = true,
  closeLabel = 'Close',
  footer,
  children,
}: SectionProps & { titleId: string }) {
  const styles = popoverStyles();
  return (
    <>
      {title !== undefined && (
        <>
          <div data-slot="popover-header" className={styles.header()}>
            <h2 id={titleId} data-slot="popover-title" className={styles.title()}>
              {title}
            </h2>
            {showClose && (
              <PopoverClose asChild>
                <IconButton
                  data-slot="popover-close"
                  icon={X}
                  size="sm"
                  aria-label={closeLabel}
                />
              </PopoverClose>
            )}
          </div>
          <Separator data-slot="popover-divider" className={styles.divider()} />
        </>
      )}
      <div data-slot="popover-body" className={styles.body()}>
        {children}
      </div>
      {footer !== undefined && (
        <div data-slot="popover-footer" className={styles.footer()}>
          {footer}
        </div>
      )}
    </>
  );
}

export type PopoverContentProps = Omit<
  ComponentProps<typeof PopoverContentPrimitive>,
  'title' | 'surface'
> &
  SectionProps;

/**
 * The Figma Popover panel: an anchored, non-modal dialog 8px from its
 * trigger that flips to stay on screen. Esc, an outside click or Close
 * dismiss it and focus returns to the trigger. Named by its title
 * (`aria-labelledby`), or pass `aria-label`.
 */
export function PopoverContent({
  size,
  title,
  showClose,
  closeLabel,
  footer,
  children,
  className,
  sideOffset = 8,
  ...props
}: PopoverContentProps) {
  const titleId = useId();
  return (
    <PopoverContentPrimitive
      surface="panel"
      sideOffset={sideOffset}
      collisionPadding={8}
      aria-labelledby={title !== undefined ? titleId : undefined}
      className={popoverStyles({ size }).root({ className })}
      {...props}
    >
      <PopoverSections
        titleId={titleId}
        title={title}
        showClose={showClose}
        closeLabel={closeLabel}
        footer={footer}
      >
        {children}
      </PopoverSections>
    </PopoverContentPrimitive>
  );
}

export type PopoverPanelProps = SectionProps & {
  className?: string;
  'aria-label'?: string;
};

/**
 * The panel drawn in place, without Radix positioning or dismissal - for docs
 * and the Figma frame, where several panels show at once. Same classes as
 * `PopoverContent`.
 */
export function PopoverPanel({
  size,
  className,
  'aria-label': ariaLabel,
  ...sections
}: PopoverPanelProps) {
  const titleId = useId();
  return (
    <PopoverRoot>
      <div
        role="group"
        data-slot="popover-content"
        aria-labelledby={sections.title !== undefined ? titleId : undefined}
        aria-label={ariaLabel}
        className={popoverStyles({ size }).root({ className })}
      >
        <PopoverSections titleId={titleId} {...sections} />
      </div>
    </PopoverRoot>
  );
}

/**
 * Anchored, non-modal panel for quick settings, filters and pickers (Figma:
 * Components ↳ Popover). Compose `Popover` › `PopoverTrigger` (`asChild`
 * around a Button) › `PopoverContent`. Keep it to one screen of content;
 * longer flows belong in a Drawer or Modal.
 */
export function Popover(props: ComponentProps<typeof PopoverRoot>) {
  return <PopoverRoot {...props} />;
}

export { PopoverAnchor, PopoverClose, PopoverTrigger };
